import {Injectable} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {UserSessionService} from './user-session.service';
import {PERMISSION, SingleResponse} from '@generated-backend-types';
import {NotEnoughPermissionsError} from '../errors/NotEnoughPermissionsError';
import {BaseEndpointService} from './api/base-endpoint-service';
import {ToastrService} from 'ngx-toastr';
import {catchError} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {FormErrorService} from './form-error.service';
import {EMPTY, Observable, of, switchMap} from 'rxjs';

@Injectable({providedIn: 'root'})
export class FormSubmitService {
  public constructor(
    private readonly userSessionService: UserSessionService,
    private readonly toastrService: ToastrService,
    private readonly translate: TranslateService,
    private readonly formErrorService: FormErrorService,
  ) {}

  public submit<Resource extends BaseResource, Data, EndpointService extends BaseEndpointService<Resource, Data>, FormData = Data>(params: {
    form: FormGroup,
    service: EndpointService,
    modelId?: string,
    payload?: Data,
    dataTransformer?: (data: FormData) => Data,
    createPermission?: PERMISSION | PERMISSION[],
    updatePermission?: PERMISSION | PERMISSION[],
    createSuccessMessage?: string,
    updateSuccessMessage?: string,
    createErrorMessage?: string,
    updateErrorMessage?: string,
    customFunction?: (modelId: string, data: FormData) => Observable<SingleResponse<Resource>>
    customFunctionPermission?: PERMISSION | PERMISSION[],
    customFunctionSuccessMessage?: string,
    customFunctionErrorMessage?: string,
  }): Observable<Resource> {
    params.form.markAllAsTouched();
    params.form.updateValueAndValidity();

    const mode = params.modelId ? params.customFunction ? 'custom' : 'edit' : 'create';

    if (!params.form.valid) {
      return EMPTY;
    }

    const rawData = (params.payload ?? params.form.getRawValue()) as FormData;
    const data: Data = params.dataTransformer ? params.dataTransformer(rawData) : (rawData as unknown as Data);

    return of(params.modelId).pipe(
      switchMap(id => {
        if (id) {
          if (params.customFunction) {
            this.checkPermission(params.customFunctionPermission);
            return params.customFunction(id, rawData);
          }

          this.checkPermission(params.updatePermission);
          return params.service.update(id, data);
        }

        this.checkPermission(params.createPermission);
        return params.service.create(data);
      }),
      catchError(err => {
        if (err instanceof NotEnoughPermissionsError) {
          this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
        } else if (err instanceof HttpErrorResponse) {
          this.formErrorService.mapErrorToForm(params.form, err);
        } else {
          this.toastMessage(mode, {
            create: params.createErrorMessage,
            edit: params.updateErrorMessage,
            custom: params.customFunctionErrorMessage,
          })
        }

        throw err;
      }),
      switchMap(res => {
        params.form.markAsPristine();
        params.form.markAsUntouched();

        this.toastMessage(mode, {
          create: params.createSuccessMessage,
          edit: params.updateSuccessMessage,
          custom: params.customFunctionSuccessMessage,
        })

        return of(res.data);
      })
    );
  }

  private toastMessage(mode: 'create' | 'edit' | 'custom', messages: {
    create?: string,
    edit?: string,
    custom?: string
  }) {
    switch (mode) {
      case "create":
        if (messages.create) {
          this.toastrService.success(messages.create);
        }
        break;
      case "edit":
        if (messages.edit) {
          this.toastrService.success(messages.edit);
        }
        break;
      case "custom":
        if (messages.custom) {
          this.toastrService.success(messages.custom);
        }
        break;
    }
  }

  private checkPermission(permission?: PERMISSION | PERMISSION[]) {
    if (!permission) return;

    const permissions = Array.isArray(permission) ? permission : [permission];

    if (!this.userSessionService.hasAllPermission(permissions)) {
      throw new NotEnoughPermissionsError();
    }
  }
}
